import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useExtraBetmenListItemCardStyle } from '../../../../../utils'
import {
  PatientReferralListItem,
  ReferralPatient,
} from '../../types/patientReferrals.types'
import { ReferralFilter, UserType } from '../../../../../models/graphqlTypes'
import { usePatientReferrals } from '../../hooks/usePatientReferrals'
import { PAGE_SIZE } from '../../../../../config'
import dayjs from 'dayjs'
import {
  BetmenList,
  BetmenListHeader,
  BetmenListHeaderCell,
  BetmenListBody,
  BetmenListItemCard,
  BetmenListItemCardCell,
  BetmenListActions,
} from '../../../../../common/components'
import { formatDayjsToStringDateType } from '../../../../../utils/formatToDateType'
import { ReferralDetailsModal } from './ReferralDetailsModal'
import { ReferralStatusChip } from './ReferralStatusChip'
import { isReferralExpired } from '../../../../patient/PatientReferrals/referralStatusFilterForPatients'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useUserType } from '../../../../../common/hooks'
import { Button, Stack } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { DeleteReferralButton } from './DeleteReferralButton'

interface ReferralListProps {
  patientProfile: ReferralPatient
  filterData: ReferralFilter
  filters?: React.ReactNode
  refetchCount?: () => void
}

const ReferralList: React.FC<ReferralListProps> = ({
  patientProfile,
  filterData,
  filters,
  refetchCount,
}) => {
  const { t } = useTranslation()
  const classes = useExtraBetmenListItemCardStyle()

  const [isReferralModalOpen, setIsReferralModalOpen] = useState<boolean>(false)
  const [referralToShow, setReferralToShow] = useState<
    PatientReferralListItem | undefined
  >(undefined)

  const basicVariables = useMemo(
    () => ({
      patientId: patientProfile.id,
      filterData,
    }),
    [filterData, patientProfile.id]
  )

  const { loading, items, hasMoreItems, fetchingMore, refetch, fetchMore } =
    usePatientReferrals({
      variables: {
        ...basicVariables,
        paginationInput: {
          offset: 0,
          limit: PAGE_SIZE,
        },
      },
    })

  const fetchMoreItems = useCallback(() => {
    const offset = items.length
    fetchMore({
      variables: {
        ...basicVariables,
        paginationInput: { limit: PAGE_SIZE, offset },
      },
    })
  }, [items.length, fetchMore, basicVariables])

  useEffect(() => {
    refetch()
  }, [refetch, filterData])

  const handleModalClose = () => {
    setIsReferralModalOpen(false)
    setReferralToShow(undefined)
  }

  const userType = useUserType()

  const isDoctor = userType === UserType.Doctor

  const refetchData = () => {
    refetch()
    refetchCount?.()
  }

  return (
    <>
      <BetmenList
        loading={loading}
        dataSource={items}
        fetchMore={fetchMoreItems}
        hasMoreItem={hasMoreItems}
        fetchMoreLoading={fetchingMore}
        gridColumnSizes={[1, 3, 3, 2, 2, 1]}
        filters={
          <Stack direction="row" gap={1} alignItems="center">
            {filters}
            {isDoctor && (
              <Button
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                color="primary"
                onClick={() => setIsReferralModalOpen(true)}
                sx={{ marginLeft: 'auto' }}
              >
                {t('doctor:referral.addNewButton')}
              </Button>
            )}
          </Stack>
        }
        header={
          <BetmenListHeader>
            <BetmenListHeaderCell>
              {t('doctor:referral.header.type')}
            </BetmenListHeaderCell>
            <BetmenListHeaderCell>
              {t('doctor:referral.header.name')}
            </BetmenListHeaderCell>
            <BetmenListHeaderCell>
              {t('doctor:referral.header.doctor')}
            </BetmenListHeaderCell>
            <BetmenListHeaderCell>
              {t('doctor:referral.header.status')}
            </BetmenListHeaderCell>
            <BetmenListHeaderCell>
              {t('doctor:referral.header.expirationDate')}
            </BetmenListHeaderCell>
            <BetmenListHeaderCell></BetmenListHeaderCell>
          </BetmenListHeader>
        }
      >
        <BetmenListBody>
          {items.map((referral) => (
            <BetmenListItemCard
              key={'wp+' + referral.id}
              click={() => {
                setReferralToShow(referral)
                setIsReferralModalOpen(true)
              }}
              className={clsx(classes.container, {
                [classes.past]: isReferralExpired(referral.status),
              })}
            >
              <BetmenListItemCardCell>
                <DescriptionOutlinedIcon fontSize="large" />
              </BetmenListItemCardCell>
              <BetmenListItemCardCell>{referral.name}</BetmenListItemCardCell>
              <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
                {t('common:formattedNameFull', {
                  title: referral.doctor.title,
                  firstName: referral.doctor.firstName,
                  lastName: referral.doctor.lastName,
                })}
              </BetmenListItemCardCell>
              <BetmenListItemCardCell>
                <ReferralStatusChip status={referral.status} />
              </BetmenListItemCardCell>
              <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
                {formatDayjsToStringDateType(dayjs(referral.expirationDate))}
              </BetmenListItemCardCell>
              <BetmenListActions>
                {referral.isDeletable && (
                  <DeleteReferralButton
                    referralId={referral.id}
                    refetch={refetchData}
                  />
                )}
              </BetmenListActions>
            </BetmenListItemCard>
          ))}
        </BetmenListBody>
      </BetmenList>

      {isReferralModalOpen && patientProfile && (
        <ReferralDetailsModal
          isOpen={isReferralModalOpen}
          handleModalClose={handleModalClose}
          patientProfile={patientProfile}
          referralId={referralToShow?.id}
          refetch={refetchData}
        />
      )}
    </>
  )
}

export { ReferralList }
